import React, { useState } from 'react';
import { Box, TextField, Button, Typography, Alert, CircularProgress } from '@mui/material';
import { supabase } from '../../supabaseClient';

const MIN_LENGTH = 8;

interface Props {
  /**
   * Called once the password is set. The invite gate uses this to drop back to
   * normal routing; the /reset-password route uses it to navigate on.
   */
  onComplete: () => void;
}

/**
 * Where a recovery or invite link ends up.
 *
 * Reached two ways, and it has to work from both:
 *  - InviteGate in App.tsx, when a link lands anywhere carrying a
 *    `type=recovery` / `type=invite` hash. This is the path that works even
 *    when Supabase ignores `redirectTo`.
 *  - The /reset-password route, when the redirect allowlist is configured and
 *    Supabase honours it.
 *
 * By the time this renders, the Supabase client has already exchanged the hash
 * token for a session, so `updateUser` is authorised. Pattern borrowed from
 * ardent-scholar's set-password page.
 */
export default function SetPasswordPage({ onComplete }: Props) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (password.length < MIN_LENGTH) {
      setError(`Use at least ${MIN_LENGTH} characters.`);
      return;
    }
    if (password !== confirm) {
      setError('Those two passwords don’t match.');
      return;
    }

    setBusy(true);
    const { error: err } = await supabase.auth.updateUser({ password });
    setBusy(false);

    if (err) {
      setError(err.message);
      return;
    }

    // Drop the recovery hash so it can't be re-processed and normal routing
    // takes over. Without this, a refresh re-enters the gate.
    window.history.replaceState(null, '', window.location.pathname);
    onComplete();
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: 3,
      }}
    >
      <Box sx={{ maxWidth: 380, width: '100%' }}>
        <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 600, mb: 1 }}>
          Roamoir
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          Choose a password. You’ll use it to sign in from now on.
        </Typography>

        <form onSubmit={handleSubmit}>
          <TextField
            type="password"
            label="New password"
            fullWidth
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoFocus
            autoComplete="new-password"
            helperText={`At least ${MIN_LENGTH} characters.`}
            sx={{ mb: 2 }}
          />
          <TextField
            type="password"
            label="Confirm password"
            fullWidth
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            autoComplete="new-password"
            sx={{ mb: 2 }}
          />
          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : null}
          <Button type="submit" variant="contained" fullWidth disabled={busy}>
            {busy ? <CircularProgress size={20} color="inherit" /> : 'Set password'}
          </Button>
        </form>
      </Box>
    </Box>
  );
}
